import React, { useRef } from "react";
import styled from "styled-components";

import {
  BusinessCircles,
  BenefitTable,
  PracticeCircles,
  RiskFactors,
  ModuleHeader,
  ModuleQuizForm,
  ModuleFooter,
  ModuleSection,
  ModuleProvider,
  ModuleSectionData,
  useModuleState,
  Tooltips,
  RoleOfManagement,
} from "~/components";
import {
  useAppContext,
  useAppContextMethods,
  useGlobalUserMethods,
  useGlobalUserState,
} from "~/state";
import { upsertHealthyWorkplace } from "~/util";

interface ModuleProps {}

export const Module1: React.FC<ModuleProps> = ({}) => {
  const { nextModule } = useGlobalUserMethods();

  const { scrollToTop } = useAppContextMethods();
  const user = useGlobalUserState();

  const [state, setState, { handleSectionChange }] = useModuleState();

  const headerRef = useRef<HTMLElement>(null);
  const section1Ref = useRef<HTMLElement>(null);
  const section2Ref = useRef<HTMLElement>(null);
  const section3Ref = useRef<HTMLElement>(null);
  const section4Ref = useRef<HTMLElement>(null);
  const section5Ref = useRef<HTMLElement>(null);
  const section6Ref = useRef<HTMLElement>(null);
  const section7Ref = useRef<HTMLElement>(null);
  const section8Ref = useRef<HTMLElement>(null);
  const section9Ref = useRef<HTMLElement>(null);
  const section10Ref = useRef<HTMLElement>(null);

  const QUIZ_1 = [
    {
      question: "Workplace health promotion is...",
      options: [
        "About encouraging employees to have a healthy lifestyle as part of their everyday working life",
        "To protect employees from illness and injury happening at work",
      ],
      answer: [0],
      related: section2Ref.current,
    },
    {
      question: "What are the key risk factors affecting workplace health?",
      options: [
        "Sedentary behaviour, unhealthy eating and drinking, alcohol use, physical inactivity",
        "Passive smoking, being active, personal hygiene, diet",
        "Poor diet, stress, alcohol use and nutrition",
        "Physical inactivity, unhealthy eating and drinking, smoking, alcohol use",
      ],
      answer: [3],
      related: section4Ref.current,
    },
    {
      question:
        "Who in your workplace is the most important contributor to the success of your health and wellbeing strategies?",
      options: [
        "The finance department",
        "Senior management",
        "Your colleagues",
      ],
      answer: [1],
      related: section6Ref.current,
    },
    {
      question:
        "What are some of the benefits the workplace is likely to see from promoting health and wellbeing?",
      options: [
        "Reduction in sick leave, improved productivity, improved image and retention of staff",
        "Retention of staff, increased costs, improved productivity and reduced staff turnover",
        "Improved productivity, high staff turnover, improved image and reduction in sick leave",
      ],
      answer: [0],
      related: section3Ref.current,
    },
    {
      question:
        "Why is it important to gain management support for your program?",
      options: [
        "Management can commit people, money and other resources to the program and encourage staff to get involved by participating themselves",
        "It’s not, management don’t need to be involved in workplace health and wellbeing",
        "Management can enforce the program to ensure all employees participate in all program activities",
      ],
      answer: [0],
      related: section6Ref.current,
    },
  ];

  return (
    <ModuleProvider onSectionChange={handleSectionChange}>
      <ModuleHeader currentPage={1} totalPages={4} ref={headerRef} />

      <ModuleSection
        id="introduction"
        sectionTitle="Introduction"
        sectionNumber={1}
        ref={section1Ref}
      >
        <div className="page-header block-blueberry">
          <h1>Introduction to workplace health</h1>
        </div>
        <div className="row mb-4">
          <div className="col-md-10 col-lg-8 mx-auto">
            <div className="card bottom-arrow">
              <div className="card-body">
                <h2 className="h1 text-center text-uppercase text-warning mb-4">
                  In this section you will learn:
                </h2>
                <ul className="bullet-fulcrum bullet-gold">
                  <li>What "workplace health promotion" entails</li>
                  <li>
                    Why the workplace is a key environment for health promotion
                  </li>
                  <li>What the biggest workplace health risk factors are</li>
                  <li>The benefits of health promotion for your business</li>
                  <li>Why it is so important to gain management support</li>
                  <li>How to build a business case</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </ModuleSection>
      <ModuleSection
        id="what-is-workplace-health"
        sectionNumber={2}
        sectionTitle={`What is "Workplace Health Promotion"`}
        ref={section2Ref}
      >
        <div className="row mb-4">
          <div className="col-md-10 offset-md-1 col-lg-8 offset-lg-2">
            <h2 className="h1 text-center text-uppercase text-warning mb-4">
              What is "Workplace Health Promotion"?
            </h2>
            <div className="embed-responsive embed-responsive-16by9 mb-4">
              <iframe
                className="embed-responsive-item"
                src="https://www.youtube-nocookie.com/embed/JuiYSWYQHYM"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="video-caption lead">
              Workplace health promotion is about encouraging staff to lead a
              healthy lifestyle as part of their everyday working life.
            </p>
            <div
              className="mb-4"
              css={`
                column-count: 2;
                column-gap: 3rem;
              `}
            >
              <p>
                Occupational health and safety aims to protect workers from
                illness and injury happening at work, whereas workplace health
                promotion looks to create a work environment which helps to
                reduce the risk of developing chronic disease.
                <Tooltips id={1} />
              </p>
              <p>
                Chronic diseases are those that are prolonged in duration and
                are often associated with lifestyle behaviours.
                <Tooltips id={2} /> They include type 2 diabetes, heart disease,
                obesity and some cancers and are responsible for about 85% of
                the total burden of disease in Australia.
                <Tooltips id={3} />
              </p>
              <p>
                A successful workplace health program involves three key areas:
              </p>
              <StyledList>
                <StyledListItem>Health policy</StyledListItem>
                <StyledListItem>
                  Supportive physical environments
                </StyledListItem>
                <StyledListItem>Staff education and activities</StyledListItem>
              </StyledList>
            </div>
          </div>
        </div>
      </ModuleSection>

      {/* Why is workplace health promotion important? */}
      <ModuleSection
        id="why-is-workplace-health"
        sectionNumber={3}
        sectionTitle="Why is workplace health promotion important"
        ref={section3Ref}
        className="module1__bg1 section-arrows mb-5"
        fluid
      >
        <div className="row bg-overlay-blueberry-dark text-white px-4 py-5">
          <div className="col-md-6">
            <h4 className="h3 text-uppercase mb-4">
              Why is workplace health promotion important?
            </h4>
            <p>
              The World Health Organization recognises the workplace as a
              priority setting for health promotion.
              <Tooltips id={1} /> We spend a large proportion of our waking
              hours at work, so why not take advantage of this time to promote
              health and wellbeing?
            </p>
            <p>
              Workplace health promotion not only benefits workers by reducing
              their risk of chronic disease, but it&rsquo;s also great for
              business.
            </p>
            <strong>
              Click on the table headings to see some of the benefits for
              organisations and employees.
              <Tooltips id={4} />
            </strong>
          </div>
          <div className="col-md-6">
            <BenefitTable />
          </div>
        </div>
      </ModuleSection>

      {/* Risk Factors */}
      <ModuleSection
        id="risk-factors"
        sectionNumber={4}
        sectionTitle="Risk factors"
        ref={section4Ref}
        className="mb-4"
      >
        <RiskFactors />
      </ModuleSection>

      {/* Relevance of Industry */}
      <ModuleSection
        id="relevance-of-industry"
        sectionNumber={5}
        sectionTitle="Relevance for industry"
        ref={section5Ref}
        className="module1__bg2 section-arrows mb-5"
        fluid
      >
        <div className="bg-overlay-blueberry-dark text-white px-4 py-5">
          <h2 className="h1 text-uppercase text-center mb-4">
            Relevance for industry
          </h2>
          <div className="row">
            <div className="col-md-6">
              <p>
                All organisations are different and come with their own
                individual health risks. Factors such as long hours, night shift
                and staff needing to stay seated during their shift can all
                contribute to unhealthy behaviours.
              </p>
              <p>
                When planning your workplace health and wellbeing program, it’s
                important to think about these workplace characteristics. It may
                not always be possible to change how your workplace is run, but
                strategies could be implemented to improve healthy choices.
              </p>
            </div>
            <div className="col-md-6">
              <h5>How your business can help:</h5>
              <ul className="bullet-fulcrum bullet-gold">
                <li>
                  Consult with staff to understand their wants, needs and
                  potential barriers
                </li>
                <li>Introduce healthy policies</li>
                <li>
                  Ensure the work environment supports and encourages healthy
                  choices
                </li>
                <li>Provide education and activities around health issues</li>
              </ul>
              <p>
                For example, on-the-road workers may have limited access to
                healthy food. This could be addressed through policy and
                environment by ensuring a cooler bag is provided. This coupled
                with healthy eating education may encourage workers to bring
                healthy foods from home.
              </p>
            </div>
          </div>
        </div>
      </ModuleSection>

      {/* Role of management and gaining their support */}
      <ModuleSection
        id="role-of-management"
        sectionNumber={6}
        sectionTitle="Role of management and gaining their support"
        ref={section6Ref}
        className="bg-light section-arrows mb-5"
      >
        <RoleOfManagement />
      </ModuleSection>

      {/* Building you business case */}
      <ModuleSection
        id="building-your-business-case"
        sectionNumber={7}
        sectionTitle="Building your business case"
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf",
              title: "Toolkit for a healthier workplace",
              type: "document",
            },
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-business-case-for-workplace-health.pdf",
              type: "document",
              title: "Business case for workplace health & wellbeing",
            },
          ];
        }, [])}
        ref={section7Ref}
        className="row section-arrows mb-5"
      >
        <div className="col-12">
          <div className="px4 py-5">
            <h2 className="h1 text-center text-uppercase text-warning mb-4">
              Building your business case
            </h2>
            <div className="row mb-4">
              <div className="col-md-6">
                <p className="mb-4">
                  What goes into your business case will depend on:
                </p>
                <ul className="bullet-fulcrum bullet-gold mb-4">
                  <li>
                    Any existing business case processes in your workplace
                  </li>
                  <li>The needs of your workplace</li>
                  <li>The size of your planned initiatives</li>
                </ul>
                <p>
                  You may need to speak with your manager to help you decide on
                  the best approach, and identify existing resources to help
                  you.
                </p>
              </div>
              <div className="col-md-6">
                <p className="mb-4">
                  The advantage of preparing a business case for management is
                  that it forces you to thoroughly think about your program:
                </p>
                <ul className="bullet-fulcrum bullet-gold mb-4">
                  <li>What you intend to do and why</li>
                  <li>What you need to make it successful</li>
                  <li>How you will go about managing it</li>
                </ul>
                <p>
                  A successful business case would consist of answers to the
                  following questions.
                </p>
              </div>
            </div>
            <h3 className="text-center text-primary mb-4">
              Click the symbols below to reveal more information.
            </h3>
            <BusinessCircles />
          </div>
        </div>
      </ModuleSection>

      {/* Putting it into practice */}
      <ModuleSection
        id="putting-it-into-practice"
        sectionNumber={8}
        sectionTitle="Putting it into practice"
        ref={section8Ref}
        className="module1__bg3 section-arrows mb-5"
        fluid
      >
        <div className="bg-overlay-blueberry-dark text-white px-4 py-5 text-center">
          <h2 className="h1 d-block text-uppercase mx-auto mb-4">
            Putting it into practice
          </h2>
          <p className="d-block w-75 mx-auto mb-4">
            Policy, environment, and education and activities are integral to
            your health and wellbeing program. The combination of all three can
            result in a program that is comprehensive and successful.
            Implementing one without the other can lead to mixed messages and
            therefore result in limited behaviour change.
          </p>

          <h3 className="text-center mb-4">
            Click the symbols below to reveal more information.
          </h3>
          <PracticeCircles />
        </div>
      </ModuleSection>

      <ModuleSection
        id="workplace-health-programs"
        sectionNumber={9}
        sectionTitle="Workplace health programs in action"
        ref={section9Ref}
        className="mb-5"
      >
        <div className="col-md-10 offset-md-1 col-lg-8 offset-lg-2 d-flex flex-column align-items-center">
          <h4 className="h1 text-center text-uppercase text-warning mb-4">
            Workplace health programs in action
          </h4>
          <div className="embed-responsive embed-responsive-16by9 mb-4">
            <iframe
              className="embed-responsive-item"
              src="https://www.youtube-nocookie.com/embed/8htm6LaIK9o"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <p className="video-caption lead text-center">
            See how Steel Blue implemented their workplace health program to
            create a positive work culture, and how it has supported their
            employees to get healthier.
          </p>
        </div>
      </ModuleSection>

      <ModuleSection
        id="quiz"
        sectionNumber={10}
        sectionTitle="Quiz"
        className="px-3"
        ref={section10Ref}
      >
        <ModuleQuizForm
          quizHeading={`Answer the following ${
            QUIZ_1.length
          } questions to progress to module ${user.currentModule + 1}`}
          quizKey={1}
          data={QUIZ_1}
          onSuccess={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              completedModules: Array.from(
                { length: user.currentModule },
                (v, i) => i + 1,
              ).join(","),
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
          onNextClick={(e) => {
            e.preventDefault();
            scrollToTop();
            nextModule();
          }}
          onFail={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
        />
      </ModuleSection>

      <ModuleFooter
        moduleNumber={1}
        totalSections={10}
        currentSection={state.currentSection}
        sectionTitle={state.currentSectionTitle}
        sectionData={state.currentSectionData || []}
      />
    </ModuleProvider>
  );
};

const StyledList = styled.ol`
  list-style: none;
  counter-reset: count;
  padding: 0;
`;

const StyledListItem = styled.li`
  counter-increment: count;
  ::before {
    content: counter(count) ". ";
    font-family: FeltTipRoman, Chalkboard, Marker Felt, cursive;
    color: ${(p) => p.theme.warning};
    padding-right: 0.5rem;
  }
`;
